// ============================================================================
// FACILITATOR AVAILABILITY WARNING — warns (never blocks) when a proposed booking
// lands on a half-day with no free Tier 1 facilitator at that studio, and flags
// facilitators who are unavailable or already booked for it.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { checkFacilitatorAvailability, AvailabilityCheck, BookingDateType } from '../../services/studio-bookings';

const SHORT_DATE = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export function useFacilitatorAvailabilityCheck(params: {
  roomId: string;
  startDate: string;
  endDate: string;
  dateType: BookingDateType;
  excludeBookingId?: string;
  enabled?: boolean;
}): AvailabilityCheck | null {
  const { roomId, startDate, endDate, dateType, excludeBookingId, enabled = true } = params;
  const [check, setCheck] = useState<AvailabilityCheck | null>(null);

  useEffect(() => {
    if (!enabled || !roomId || !startDate || (endDate && endDate < startDate)) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      checkFacilitatorAvailability({ roomId, startDate, endDate: endDate || startDate, dateType, excludeBookingId })
        .then(c => { if (!cancelled) setCheck(c); })
        .catch(() => { if (!cancelled) setCheck(null); });  // non-fatal: warning only
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [roomId, startDate, endDate, dateType, excludeBookingId, enabled]);

  return check;
}

export function availabilityLabel(check: AvailabilityCheck | null, userId: string): string {
  const conflict = check?.conflicts[userId];
  if (conflict === 'unavailable') return 'unavailable';
  if (conflict === 'booked') return 'already booked';
  return '';
}

export function availabilitySuffix(check: AvailabilityCheck | null, userId: string): string {
  const label = availabilityLabel(check, userId);
  return label ? ` (${label})` : '';
}

export function FacilitatorShortfallBanner({ check, roomName }: { check: AvailabilityCheck | null; roomName?: string }) {
  if (!check?.shortfalls.length) return null;
  const shown = check.shortfalls.slice(0, 6).map(s =>
    `${SHORT_DATE.format(new Date(`${s.date}T00:00:00`))} ${s.slot === 'morning' ? 'AM' : 'PM'}`
  );
  const more = check.shortfalls.length - shown.length;
  return (
    <div className="flex gap-2.5 p-3 rounded-xl border border-amber-200 bg-amber-50">
      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-amber-800">
        <span className="font-semibold">No Tier 1 facilitator free{roomName ? ` at ${roomName}` : ''}:</span>{' '}
        {shown.join(', ')}{more > 0 ? ` and ${more} more` : ''}. You can still book, but confirm facilitator cover first.
      </p>
    </div>
  );
}
